import { useAppDispatch, useAppSelector } from "app/hooks"
import { Map } from "leaflet"
import { RefObject } from "react"
import { usePollControllers } from "services/controllerApi"
import {
  selectAirwayLowerUpper,
  selectAirwayOnMap,
  selectAreasDFSOnMap,
  selectAreasOnMap,
  selectAreasVLARAOnMap,
  selectDFSOnMap,
  selectDWDOnMap,
  selectLightning,
  selectLoaOnMap,
  selectOpenFlightmapsOnMap,
  selectSatelliteOnMap,
  selectSectorsOnMap,
  selectSigmetOnMap,
  selectWindOnMap,
  setAirwayLowerUpper,
  setAirwayOnMap,
  setAreas,
  setAreasDFS,
  setAreasVLARA,
  setDFS,
  setDWD,
  setLightning,
  setLoa,
  setOpenFlightmaps,
  setSatellite,
  setSectors,
  setSigmet,
  setWind,
} from "services/mapSlice"
import { Button, Flex } from "theme-ui"
import { SectorControls } from "../SectorControls"
import { LevelChoice } from "./LevelChoice"
import { WindLevelChoice } from "./WindLevelChoice"
import { Search } from "./search/Search"

export const MapControls = ({ map }: { map: RefObject<Map> }) => {
  const dispatch = useAppDispatch()

  usePollControllers()

  const ofm = useAppSelector(selectOpenFlightmapsOnMap)
  const dfs = useAppSelector(selectDFSOnMap)
  const dwd = useAppSelector(selectDWDOnMap)
  const satellite = useAppSelector(selectSatelliteOnMap)
  const lightning = useAppSelector(selectLightning)
  const sectors = useAppSelector(selectSectorsOnMap)
  const areas = useAppSelector(selectAreasOnMap)
  const areas_dfs = useAppSelector(selectAreasDFSOnMap)
  const areas_vlara = useAppSelector(selectAreasVLARAOnMap)
  const loa = useAppSelector(selectLoaOnMap)
  const airways = useAppSelector(selectAirwayOnMap)
  const airwayLowerUpper = useAppSelector(selectAirwayLowerUpper)
  const sigmet = useAppSelector(selectSigmetOnMap)
  const wind = useAppSelector(selectWindOnMap)

  return (
    <Search map={map}>
      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"></polygon>
          <line x1="8" y1="2" x2="8" y2="18"></line>
          <line x1="16" y1="6" x2="16" y2="22"></line>
        </svg>
        <Button
          variant={ofm ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setOpenFlightmaps(!ofm))}
        >
          OFM
        </Button>
        <Button
          variant={dfs ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setDFS(!dfs))}
        >
          ICAO
        </Button>
        <Button
          variant={satellite ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setSatellite(!satellite))}
        >
          SAT
        </Button>
      </Flex>
      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 16.9A5 5 0 0 0 18 7h-1.26a8 8 0 1 0-11.62 9"></path>
          <polyline points="13 11 9 17 15 17 11 23"></polyline>
        </svg>
        <Button
          variant={dwd ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setDWD(!dwd))}
        >
          DWD Precip
        </Button>
        <Button
          variant={lightning ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setLightning(!lightning))}
        >
          Lightning
        </Button>
        <Button
          variant={sigmet ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setSigmet(!sigmet))}
        >
          SIGMET
        </Button>
      </Flex>
      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9.59 4.59A2 2 0 1 1 11 8H2"></path>
          <path d="M12.59 11.59A2 2 0 1 1 14 15H2"></path>
          <path d="M10.59 17.59A2 2 0 1 1 12 21H2"></path>
        </svg>
        <Button
          variant={wind ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setWind(!wind))}
        >
          Wind
        </Button>
      </Flex>
      {wind && <WindLevelChoice />}
      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="4"></circle>
          <line x1="1.05" y1="12" x2="7" y2="12"></line>
          <line x1="17.01" y1="12" x2="22.96" y2="12"></line>
        </svg>
        <Button
          variant={airways ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setAirwayOnMap(!airways))}
        >
          Airways
        </Button>
        <Button
          variant={
            airwayLowerUpper === "LOWER"
              ? "selectedSecondaryNav"
              : "secondaryNav"
          }
          sx={{
            flex: 1,
            visibility: airways ? "visible" : "hidden",
          }}
          onClick={() => dispatch(setAirwayLowerUpper("LOWER"))}
        >
          lower
        </Button>
        <Button
          variant={
            airwayLowerUpper === "UPPER"
              ? "selectedSecondaryNav"
              : "secondaryNav"
          }
          sx={{ flex: 1, visibility: airways ? "visible" : "hidden" }}
          onClick={() => dispatch(setAirwayLowerUpper("UPPER"))}
        >
          upper
        </Button>
      </Flex>

      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
          <polyline points="2 17 12 22 22 17"></polyline>
          <polyline points="2 12 12 17 22 12"></polyline>
        </svg>
        <Button
          variant={sectors ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setSectors(!sectors))}
        >
          Sectors
        </Button>
        <Button
          variant={loa ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setLoa(!loa))}
        >
          LOA
        </Button>
        <Button
          variant={areas ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1, visibility: "hidden" }}
          onClick={() => dispatch(setAreas(!areas))}
        >
          Areas
        </Button>
      </Flex>
      <Flex
        sx={{ justifyContent: "space-evenly", gap: 2, alignItems: "center" }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="16" y1="2" x2="16" y2="6"></line>
          <line x1="8" y1="2" x2="8" y2="6"></line>
          <line x1="3" y1="10" x2="21" y2="10"></line>
        </svg>
        <Button
          variant={areas ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1 }}
          onClick={() => dispatch(setAreas(!areas))}
        >
          Areas
        </Button>
        <Button
          variant={areas_dfs ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1, visibility: areas ? "visible" : "hidden" }}
          onClick={() => dispatch(setAreasDFS(!areas_dfs))}
        >
          DFS
        </Button>
        <Button
          variant={areas_vlara ? "selectedSecondaryNav" : "secondaryNav"}
          sx={{ flex: 1, visibility: areas ? "visible" : "hidden" }}
          onClick={() => dispatch(setAreasVLARA(!areas_vlara))}
        >
          VATSIM
        </Button>
      </Flex>
      {(areas || sectors || loa) && <LevelChoice />}
      {sectors && <SectorControls />}
    </Search>
  )
}
